import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { anonymousNickname } from '@/lib/nickname';
import { useUsersMeQuery, useUpdateUserMutation } from '@/services/endpoints/users';
import { useTelegram } from '@/hooks/useTelegram';
import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';

interface WelcomeStepProps {
  onNext: () => void;
}

/**
 * Welcome + nickname. Prefills with the Telegram first name; saving is
 * best-effort (PUT /users/:id with the user's current record spread back so
 * the Laravel endpoint gets its full body). Skip keeps an existing real
 * nickname; if the current one is empty or email-derived (the reconciler's
 * auto-nickname leaks the email prefix) it writes `anonymous#NNNNNN` instead.
 */
export function WelcomeStep({ onNext }: WelcomeStepProps) {
  const { t, i18n } = useTranslation('onboarding');
  const { user: tgUser } = useTelegram();
  const { data: me, isLoading: meLoading } = useUsersMeQuery();
  const [updateUser, { isLoading: saving }] = useUpdateUserMutation();
  const haptics = useHaptics();
  const [nickname, setNickname] = useState('');
  const [touched, setTouched] = useState(false);

  const emailPrefix = (me?.email ?? '').split('@')[0];
  const hasRealNickname = !!me?.nickname && me.nickname !== emailPrefix;
  const prefill = hasRealNickname ? (me?.nickname ?? '') : (tgUser?.first_name ?? '');
  const shown = touched ? nickname : prefill;

  const save = async (finalNick: string) => {
    if (me && finalNick && finalNick !== me.nickname) {
      try {
        await updateUser({
          id: me.id,
          nickname: finalNick,
          country: me.country ?? '',
          birthday: me.birthday ?? '',
          name: me.name ?? '',
          accepted_privacy: me.accepted_privacy ?? true,
          locale: me.locale ?? i18n.language,
        }).unwrap();
        haptics.notification('success');
      } catch {
        /* best-effort — never block the wizard on a profile write */
      }
    }
    onNext();
  };

  const submit = () => void save(shown.trim() || anonymousNickname());
  const skip = () => void save(hasRealNickname ? '' : anonymousNickname());

  useMainButton({
    text: t('welcome.cta'),
    enabled: !saving && !meLoading,
    loading: saving || meLoading,
    onClick: submit,
  });

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-3xl font-bold text-brand">{t('welcome.title')}</h1>
      <p className="text-sm text-fg-muted">{t('welcome.body')}</p>
      <label className="flex w-full flex-col gap-2 text-left">
        <span className="text-sm font-semibold text-fg">{t('welcome.nicknameLabel')}</span>
        <input
          type="text"
          value={shown}
          maxLength={32}
          onChange={(e) => {
            setTouched(true);
            setNickname(e.target.value);
          }}
          className="rounded-xl border border-border bg-bg-secondary p-3 text-sm text-fg outline-none focus:border-brand"
        />
      </label>
      <button
        type="button"
        onClick={skip}
        disabled={saving || meLoading}
        className="text-xs font-semibold text-fg-muted underline"
      >
        {t('welcome.skip')}
      </button>
    </div>
  );
}
